import { Component, type ReactNode } from 'react';

export interface ErrorBoundaryProps {
  /** Rendered instead of the children after an error; `reset` renders them again. */
  fallback: (reset: () => void, error: Error) => ReactNode;
  /** When any of these change, a failed boundary resets itself (e.g. new data to draw). */
  resetKeys?: readonly unknown[];
  children: ReactNode;
}

interface State {
  error: Error | null;
  keys: readonly unknown[];
}

const changed = (a: readonly unknown[], b: readonly unknown[]): boolean =>
  a.length !== b.length || a.some((v, i) => !Object.is(v, b[i]));

/**
 * Contains a rendering error to one part of the UI (a chart, a view, the app). The rest of the
 * app keeps working.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, State> {
  override state: State = { error: null, keys: this.props.resetKeys ?? [] };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  static getDerivedStateFromProps(props: ErrorBoundaryProps, state: State): Partial<State> | null {
    const keys = props.resetKeys ?? [];
    if (state.error && changed(keys, state.keys)) return { error: null, keys };
    return changed(keys, state.keys) ? { keys } : null;
  }

  private readonly reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    return this.state.error
      ? this.props.fallback(this.reset, this.state.error)
      : this.props.children;
  }
}
